import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ProviderInfo } from '@app/manage-providers/provider-api.model';
import { OrgProvidersService } from '@app/orgs/services/org-providers.service';
import { SearchOrigin } from '@app/search/services/search-tracker.service';
import { SearchService } from '@app/search/services/search.service';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { NUM_TILE_RESULTS_PER_PAGE } from '@app/shared/search.token';
import { AuthService } from '@app/shared/services/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';
import { SearchFiltersStateService } from '../search-filters/search-filters-state.service';
import { ContentCardService } from '@app/shared/components/content/content-card/content-card.service';
import { SearchFilterId } from '@app/search/services';

export interface SearchProviderCustomProperties {
  providerId: number;
  providerName: string;
}

@Component({
  selector: 'dgx-search-provider-view',
  templateUrl: './search-provider-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchProviderViewComponent
  extends SubscriberBaseDirective
  implements OnInit
{
  public provider: ProviderInfo;
  public title: string;
  public data: Observable<any>;
  public hasError = false;
  public searchOrigin = SearchOrigin.provider;
  public customTrackerProperties: SearchProviderCustomProperties;
  public genericTitle = this.translateService.instant('Core_Provider');
  public filterParam: string;
  public indexesOfExpandedCards$: Observable<number[]>;

  private providerId: number;
  private orgId: number;

  constructor(
    private searchService: SearchService,
    private authService: AuthService,
    private translateService: TranslateService,
    private route: ActivatedRoute,
    private orgProvidersService: OrgProvidersService,
    private searchFiltersStateService: SearchFiltersStateService,
    private contentCardService: ContentCardService,
    @Inject(NUM_TILE_RESULTS_PER_PAGE)
    private readonly numResultsPerPage: number
  ) {
    super();
  }

  public get filterIdsToIgnore() {
    return [SearchFilterId.Provider];
  }

  public ngOnInit(): void {
    this.searchFiltersStateService.setExtractLanguageFilter(true);
    this.route.queryParams.subscribe((params) => {
      this.providerId = params.providerId;
      this.filterParam = `Provider:${this.providerId}`;

      const isExternalCat =
        params.isExternalCat && params.isExternalCat === 'true';

      this.orgId = isExternalCat
        ? null
        : this.authService.authUser?.defaultOrgId;

      this.orgProvidersService
        .getProvider(this.providerId, this.orgId)
        .pipe(
          finalize(() => {
            this.title = this.provider?.providerName || this.genericTitle;
          })
        )
        .subscribe((provider) => {
          this.provider = provider;
          this.customTrackerProperties = {
            providerId: provider.providerId,
            providerName: provider.providerName,
          };
        });
    });

    this.indexesOfExpandedCards$ =
      this.contentCardService.getIndexesOfExpandedCards();
  }

  public search({
    term,
    numResultsToSkip,
    filters,
    sortBy,
    orgId,
  }: {
    term: string;
    numResultsToSkip: number;
    filters?: any[];
    sortBy?: string;
    orgId: number;
  }) {
    const isRecent = sortBy && sortBy === 'recent';
    const isPopular = sortBy && sortBy === 'popular';

    this.data = this.searchService
      .search(
        term,
        filters,
        this.numResultsPerPage,
        numResultsToSkip,
        orgId,
        isRecent,
        isPopular,
        this.searchFiltersStateService.ignorePreferredLanguage
      )
      .pipe(
        map((data) => {
          // We don't need the Provider filter
          const filters = data.filters.filter(
            ({ model }) => model.id !== SearchFilterId.Provider
          );
          return {
            ...data,
            filters,
          };
        }),
        // Replay the stream for the many async pipes used in the markup
        shareReplay({ bufferSize: 1, refCount: true }),
        catchError(() => {
          this.hasError = true;
          // Return EMPTY here rather than of(null); EMPTY
          // is a "never" stream, perfect for when we want
          // to end the stream by returning an Observable
          // with no content.
          return EMPTY;
        })
      );
  }
}
